import { useState } from 'react';
import type { ICustomControlProcedureDiagnosticReportAddEditProps } from '../../../interfaces/';
import { ProcessEntity } from '../../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../../common/enums/toastType';
import { translateKey } from '../../../../../../translate';
import { useEmailAddressControlComponent } from '../../../../../../utils/hooks/useEmailAddressControlComponent';
import { useEpisodeContext } from '../../../../../../utils/hooks/useEpisodeContext';
import { renameErrorKeys } from '../../../../../../utils/renameErrorKeys';
import { showServiceToast } from '../../../../../../utils/showToast';
import { CancelButton } from '../../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Form } from '../../../../../elements/common';
import { ProcessContainer } from '../../../../episodes/common';
import { NewOrEditDiagnosticReportProcessContainer } from '../../../utils/v1/NewOrEditDiagnosticReportProcessContainer';
import { PilotExaminationV1DiagnosticReportAddEditCustomControlFields } from './PilotExaminationV1DiagnosticReportAddEditCustomControlFields';
import { PilotExaminationV1ProcedureAddEdit } from './PilotExaminationV1ProcedureAddEdit';

export function PilotExaminationV1ProcedureDiagnosticReportAddEdit(
    props: ICustomControlProcedureDiagnosticReportAddEditProps
) {
    const {
        procedure,
        diagnosticReport,
        errors,
        handleChangeCustomControl,
        handleChangeCustomControlDiagnosticReport,
        validateOneCustomControl,
        validateOneCustomControlDiagnosticReport,
    } = props;

    const { patient } = useEpisodeContext();
    const { EmailAddressControlProps, EmailAddressControl, validateEmailAddress } = useEmailAddressControlComponent({
        emailAddress: procedure.customControlData.emailadres
            ? procedure.customControlData.emailadres
            : patient.emailAddress,
        handleChangeCustomControl,
        validateOneCustomControl,
    });
    const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);

    return (
        <>
            <PilotExaminationV1ProcedureAddEdit
                {...props}
                isBioGrader
            />

            <Form onSubmit={() => null}>
                <NewOrEditDiagnosticReportProcessContainer
                    currentDiagnosticReport={props.currentDiagnosticReport}
                    service={props.service}
                >
                    <PilotExaminationV1DiagnosticReportAddEditCustomControlFields
                        customControlData={diagnosticReport.customControlData}
                        errors={renameErrorKeys(errors, 'diagnosticReport.customControlData', 'customControlData')}
                        handleChangeCustomControl={handleChangeCustomControlDiagnosticReport}
                        validateOneCustomControl={validateOneCustomControlDiagnosticReport}
                    />
                </NewOrEditDiagnosticReportProcessContainer>

                {props.request.isDirectProcedureAdded && (
                    <EmailAddressControl
                        {...EmailAddressControlProps}
                        error={errors.get('diagnosticReport.customControlData.emailadres')}
                    />
                )}

                <ProcessContainer noBorderTop>
                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                cancel={props.onCancel}
                                entity={ProcessEntity.procedure}
                                isExisting={!!props.request.id}
                                serviceCode={props.service.code}
                                serviceType={props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={buttonDisabled}
                                save={async () => {
                                    await saveProcedure('draft');
                                }}
                            />,
                            <SaveSendButton
                                buttonDisabled={buttonDisabled}
                                saveSend={async () => {
                                    await saveProcedure('final');
                                }}
                            />,
                        ]}
                    />
                </ProcessContainer>
            </Form>
        </>
    );

    async function saveProcedure(saveType: 'draft' | 'final') {
        setButtonDisabled(true);

        const validatedProcedure = await props.validateProcedure(saveType);
        if (!validatedProcedure) {
            setButtonDisabled(false);

            return;
        }

        if (props.request.isDirectProcedureAdded) {
            const isValidatedEmailAddress = validateEmailAddress();
            if (!isValidatedEmailAddress) {
                setButtonDisabled(false);

                return;
            }
        }

        const requestId = await props.onSaveProcedure(saveType);
        setButtonDisabled(false);
        if (!requestId) {
            return;
        }

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'procedure',
                translateKey('toast.submitDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'procedure',
                translateKey('toast.draftDefaultMessage')
            );
        }

        await props.onSave(requestId);
    }
}
